"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Layers, MessageSquareText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const steps = [{
  icon: Layers,
  title: "Browse by category",
  description: "Filter questions by specific focus areas, from retirement income strategies to annuity basics. Find answers structured around the questions people ask most.",
  anchor: "#categories"
}, {
  icon: BookOpen,
  title: "Unpack the details",
  description: "Review clear explanations of product rules, trade-offs, and timelines. Each topic breaks down complex terms into straightforward guidance.",
  anchor: "#faq-list"
}, {
  icon: MessageSquareText,
  title: "Request personalized analysis",
  description: "Connect with licensed professionals for a tailored scenario review. Get objective calculations based on your specific financial situation.",
  anchor: "/ContactPage"
}];
export default function Introduction() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        delayChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="introduction" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12">
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-3xl text-center">
          <div className="flex items-center justify-center">
            <Badge variant="outline" className="rounded-full border-border bg-muted/60 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-foreground">
              Knowledge Center Guide
            </Badge>
          </div>

          <h2 className="font-default text-foreground mt-4 text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
            How to navigate our Knowledge Center
          </h2>

          <div className="mx-auto mt-4 flex items-center justify-center gap-2">
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
          </div>

          <p className="text-muted-foreground mt-5 text-base leading-relaxed sm:text-lg">
            Explore clear, straightforward information across key insurance and retirement topics at your own pace.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((step, i) => {
          const IconComponent = step.icon;
          return <motion.div key={step.title} variants={itemVariants} data-index={i}>
                <Card className="border-border bg-card shadow-xs group relative h-full rounded-xl border transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <div className="bg-primary/20 group-hover:bg-primary absolute inset-x-0 top-0 h-1 rounded-t-xl transition-colors duration-200" />
                  <CardContent className="flex h-full flex-col justify-between p-6 sm:p-7">
                    <div>
                      <div className="bg-muted text-foreground flex size-12 items-center justify-center rounded-lg border border-border">
                        <IconComponent className="size-6 stroke-[1.75]" />
                      </div>

                      <h3 className="font-default text-card-foreground mt-5 text-xl font-semibold tracking-tight">
                        {step.title}
                      </h3>

                      <p className="text-muted-foreground mt-3 text-sm leading-relaxed sm:text-base">
                        {step.description}
                      </p>
                    </div>

                    <div className="pt-6">
                      <Link to={step.anchor} className="text-primary font-default inline-flex items-center text-sm font-semibold tracking-tight underline-offset-4 hover:underline">
                        Explore step
                        <span className="ml-1 transition-transform group-hover:translate-x-0.5">
                          &rarr;
                        </span>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}